'use client';

import { useCallback, useMemo, useState, type ReactNode } from 'react';
import type { ExternalNetworkEntityKind } from '@terra/shared-contracts-external-network';
import { Box } from '@terra/ui-styled-system';
import {
  DETECTION_VIEW,
  ListPageLayout,
  ListPageOptionsSwitch,
  type DetectionView,
} from '@terra/ui-design-system';
import { Radar01Icon, Search01Icon } from '@terra/ui-icons';
import { useDebounceValue } from 'usehooks-ts';
import { useTranslations } from 'next-intl';
import { EmptyState } from '../../EmptyState';
import {
  getExternalNetworkEntityCount,
  translateExternalNetworkFilterItems,
  type ExternalNetworkEntityKindFilter,
  buildSurfaceAssetTabId,
} from './surfaceAsset.constants';
import { useExternalNetworkStats } from '../../../hooks/api/useExternalNetworkStats';
import type { OpenMainContentTabInput } from '../../../store/layoutStore';
import { useMainContentTabs } from '../../../store/layoutStore';
import { TARGET_TYPE } from '../../../utils/target-scope.utils';
import { useExternalNetworkAssetListFilters } from './useExternalNetworkAssetListFilters';
import { enetAssetListKindFromFilter } from './asset-list-columns';
import { SurfaceAssetsContextNavigation } from './SurfaceAssetsContextNavigation';
import { useExternalAssetsGroupByLabel } from './useExternalAssetsGroupByLabel';
import { ExternalNetworkAssetsFilterPanel } from './ExternalNetworkAssetsFilterPanel';
import { ExternalAssetsTable } from './external-assets-table/ExternalAssetsTable';
import {
  resolveExternalAssetsGroupBy,
  resolveExternalAssetsGroupByOptions,
  type ExternalAssetGridItem,
  type ExternalAssetsGroupByField,
  type ExternalAssetsSort,
} from './external-assets-table/external-assets-table.model';
import { useExternalAssetsListData } from './external-assets-table/useExternalAssetsListData';
import { useExternalAssetsTableRows } from './external-assets-table/useExternalAssetsTableRows';
import { resolveSurfaceAssetKindCount } from './surface-asset-kind-count';

type ExternalNetworkAssetsListProps = {
  surfaceId: string;
  header?: ReactNode;
};

const EMPTY_KIND_KEY: Record<ExternalNetworkEntityKind, string> = {
  NETBLOCK: 'netblocks',
  DOMAIN: 'domains',
  HOST: 'hosts',
  GATEWAY: 'gateways',
  NETWORK_SERVICE: 'services',
  SOFTWARE: 'software',
  STORAGE_SERVICE: 'storage',
};

export function buildExternalNetworkAssetTab({
  entityKind,
  entityId,
  label,
}: {
  entityKind: ExternalNetworkEntityKind;
  entityId: string;
  label: string;
}): OpenMainContentTabInput {
  return {
    id: buildSurfaceAssetTabId({
      targetType: TARGET_TYPE.EXTERNAL_NETWORK,
      surfaceAssetKind: entityKind,
      entityId,
    }),
    title: label,
    type: 'surface-asset',
    data: {
      surfaceAssetKind: entityKind,
      entityId,
      targetType: TARGET_TYPE.EXTERNAL_NETWORK,
    },
  };
}

export function ExternalNetworkAssetsList({
  surfaceId,
  header,
}: ExternalNetworkAssetsListProps) {
  const t = useTranslations('externalNetwork.assetList');
  const tViewOptions = useTranslations('proxy.assets.viewOptions');
  const groupByLabel = useExternalAssetsGroupByLabel();
  const tEntityKinds = useTranslations('externalNetwork.entityKinds');
  const { navigateInActiveTab } = useMainContentTabs();
  const filterItems = useMemo(
    () => translateExternalNetworkFilterItems({ t: tEntityKinds }),
    [tEntityKinds],
  );
  const { stats } = useExternalNetworkStats(surfaceId);
  const [selectedKind, setSelectedKind] =
    useState<ExternalNetworkEntityKindFilter>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [detectionView, setDetectionView] = useState<DetectionView>(
    DETECTION_VIEW.ACTIVE,
  );
  const [sort, setSort] = useState<ExternalAssetsSort>(null);
  const [requestedGroupBy, setRequestedGroupBy] =
    useState<ExternalAssetsGroupByField | null>(null);
  const [debouncedSearch] = useDebounceValue(searchTerm, 300);
  const {
    assetFilterRowConfig,
    createdAtDateFilterDialog,
    listFilters,
    hasListFilters,
  } = useExternalNetworkAssetListFilters({
    selectedKind,
    surfaceId,
  });
  const groupBy = resolveExternalAssetsGroupBy({
    kind: selectedKind,
    groupBy: requestedGroupBy,
  });
  const hasSearchOrListFilters =
    debouncedSearch.trim().length > 0 || hasListFilters;
  const listData = useExternalAssetsListData({
    surfaceId,
    selectedKind,
    search: debouncedSearch,
    filters: listFilters,
    sort,
    groupBy,
    includeKindTotals: hasSearchOrListFilters,
  });
  const tableRows = useExternalAssetsTableRows({
    entries: listData.entries,
    selectedKind,
    groupBy,
    groups: listData.groups,
    kindTotals: listData.kindTotals,
    resetKey: `${selectedKind ?? 'ALL'}:${groupBy ?? ''}`,
  });
  const totalAssetCount = getExternalNetworkEntityCount({ stats, kind: null });
  const hasActiveFilter = selectedKind !== null || hasSearchOrListFilters;
  const listKind = enetAssetListKindFromFilter({ selectedKind });
  const emptyKindKey = selectedKind ? EMPTY_KIND_KEY[selectedKind] : 'all';
  const handleSelectKind = useCallback(
    (kind: ExternalNetworkEntityKindFilter) => {
      setSelectedKind(kind);
      setSort(null);
    },
    [],
  );
  const handleOpenAsset = useCallback(
    (item: ExternalAssetGridItem) => {
      if (!item.id) {
        return;
      }

      navigateInActiveTab(
        buildExternalNetworkAssetTab({
          entityKind: item.entityKind,
          entityId: item.id,
          label: item.label,
        }),
      );
    },
    [navigateInActiveTab],
  );
  const onAssetFilterChange = assetFilterRowConfig.onFilterChange;
  const handleClearFilters = useCallback(() => {
    setSearchTerm('');
    onAssetFilterChange([]);
  }, [onAssetFilterChange]);
  const hasToolbarFilters =
    searchTerm.trim().length > 0 || assetFilterRowConfig.filterValue.length > 0;
  const { kindTotals, kindTotalsSum } = listData;
  const filteredKindCounts = useMemo(
    () =>
      hasSearchOrListFilters
        ? { byKind: kindTotals, total: kindTotalsSum }
        : null,
    [hasSearchOrListFilters, kindTotals, kindTotalsSum],
  );
  const getKindCount = useCallback(
    (kind: ExternalNetworkEntityKindFilter) =>
      resolveSurfaceAssetKindCount({
        kind,
        filteredCounts: filteredKindCounts,
        statsCount: getExternalNetworkEntityCount({ stats, kind }),
      }),
    [filteredKindCounts, stats],
  );
  const groupByOptions = useMemo(
    () =>
      resolveExternalAssetsGroupByOptions(selectedKind).map((option) => ({
        value: option,
        label: groupByLabel(option),
      })),
    [groupByLabel, selectedKind],
  );
  const isEmpty =
    !listData.isLoading && !listData.isError && tableRows.rows.length === 0;

  return (
    <ListPageLayout
      header={header}
      navigation={
        <SurfaceAssetsContextNavigation
          items={filterItems}
          selectedKind={selectedKind}
          onSelectKind={handleSelectKind}
          getCount={getKindCount}
          searchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
          searchPlaceholder={t('searchPlaceholder')}
          onRefresh={listData.refresh}
          isLoading={listData.isLoading}
          groupByOptions={groupByOptions}
          groupBy={groupBy ?? null}
          onGroupByChange={setRequestedGroupBy}
          areAllGroupsCollapsed={tableRows.areAllGroupsCollapsed}
          onExpandAllGroups={tableRows.expandAllGroups}
          onCollapseAllGroups={tableRows.collapseAllGroups}
        >
          <ListPageOptionsSwitch
            label={tViewOptions('hideDoneDetections')}
            checked={detectionView === DETECTION_VIEW.ACTIVE}
            onCheckedChange={(checked) =>
              setDetectionView(
                checked ? DETECTION_VIEW.ACTIVE : DETECTION_VIEW.ALL,
              )
            }
          />
        </SurfaceAssetsContextNavigation>
      }
      filters={
        <ExternalNetworkAssetsFilterPanel
          filterRow={assetFilterRowConfig}
          hasActiveFilters={hasToolbarFilters}
          filteredCount={listData.total}
          onClear={handleClearFilters}
        />
      }
    >
      {createdAtDateFilterDialog}
      {listData.isError ? (
        <Box flex={1} minH={0}>
          <EmptyState
            icon={Radar01Icon}
            title={t('loadError')}
            description={t('loadErrorDescription')}
          />
        </Box>
      ) : isEmpty ? (
        <Box flex={1} minH={0}>
          <EmptyState
            icon={hasActiveFilter ? Search01Icon : Radar01Icon}
            title={
              totalAssetCount === 0 && !hasActiveFilter
                ? t('noAssetsYet')
                : t('noMatching')
            }
            description={
              totalAssetCount === 0 && !hasActiveFilter
                ? t(`empty.${emptyKindKey}`)
                : t('noMatchingDescription')
            }
          />
        </Box>
      ) : (
        <ExternalAssetsTable
          listKind={listKind}
          rows={tableRows.rows}
          loadMoreRowIndex={tableRows.loadMoreRowIndex}
          sort={sort}
          detectionView={detectionView}
          collapsedGroupKeys={tableRows.collapsedGroupKeys}
          isLoading={listData.isLoading}
          isFetchingMore={listData.isFetchingMore}
          hasMore={listData.hasMore}
          onLoadMore={listData.loadMore}
          onSortChange={setSort}
          onToggleGroup={tableRows.toggleGroup}
          onOpenAsset={handleOpenAsset}
        />
      )}
    </ListPageLayout>
  );
}
